import {AlertTriangle, OctagonAlert} from 'lucide-react';
import {cn} from '@/lib/utils/cn';
import {Input} from '@/components/ui/Input';
import {Textarea} from '@/components/ui/Textarea';
import {getElementDefinition} from './lib/dictionary';
import {Explain} from './Explain';
import type {EdiSegment} from './lib/tokenizer';
import type {EdiTransactionExplained} from './lib/explainedEnvelope';
import type {ValidationFinding} from './lib/validator';
import type {ElementPath} from './lib/document-editor';
import {elementFindingPath, elementPathFor} from './lib/segmentPaths';

interface SegmentFieldProps {
  groupIndex: number;
  transactionIndex: number;
  transaction: EdiTransactionExplained;
  segment: EdiSegment;
  /** 1-indexed element position on `segment`, e.g. 3 for BEG03. */
  position: number;
  /** This segment's finding-path base, e.g. "groups[0].transactions[0].header.beg". */
  basePath: string;
  findings: ValidationFinding[];
  /** Read-only inline text when false; a compact input when true. */
  editing: boolean;
  autoFocus?: boolean;
  onEdit: (path: ElementPath, value: string) => void;
}

/** Native <datalist> has no virtualization — with the Product/Service ID
 * Qualifier list's ~500+ entries shared across every PO1/SLN qualifier
 * position, rendering the full list as <option> nodes on every edited field
 * at once gets expensive. Suggestions are capped; the full list still backs
 * validation and the code-meaning lookup regardless of what's suggested
 * here. */
const MAX_DATALIST_OPTIONS = 100;

function isLongTextField(label: string): boolean {
  const lower = label.toLowerCase();
  return (
    lower.includes('description') ||
    lower.includes('message') ||
    lower.includes('text')
  );
}

/** Drops the validator's leading "BEG01 (Label) value "X"" from a message
 * shown right beside that same label and value, e.g. leaving "Not one of
 * this dictionary's known codes." */
function inlineMessage(message: string): string {
  const rest = message.replace(/^\S+ \([^)]*\)(?: value ".*?")? (?:is )?/, '');
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/** Whether a read-only field is worth showing: it has a value, or a finding
 * (e.g. a required element left empty) that the user needs to see. */
export function isFieldVisible(
  segment: EdiSegment,
  position: number,
  basePath: string,
  findings: ValidationFinding[]
): boolean {
  const raw = segment.elements[position - 1];
  const hasValue = Array.isArray(raw) ? raw.some(Boolean) : Boolean(raw);
  const path = elementFindingPath(basePath, position);
  return hasValue || findings.some(f => f.path === path);
}

/** One element as an aligned label/value row: the label on the left, the
 * value (and a coded value's meaning) on the right, and any finding just
 * below it. Everything explanatory — element code, required/format and the
 * dictionary's description — lives in the label's hover tooltip. While its
 * segment is being edited, the value becomes a compact input with known
 * codes offered as suggestions. */
export function SegmentField({
  groupIndex,
  transactionIndex,
  transaction,
  segment,
  position,
  basePath,
  findings,
  editing,
  autoFocus,
  onEdit,
}: SegmentFieldProps) {
  const definition = getElementDefinition(segment.id, position);
  const rawValue = segment.elements[position - 1];
  const isComposite = Array.isArray(rawValue);
  const value = isComposite ? rawValue.join(' : ') : (rawValue ?? '');

  const findingPath = elementFindingPath(basePath, position);
  const matches = findings.filter(f => f.path === findingPath);
  const severity = matches.some(f => f.severity === 'error')
    ? 'error'
    : matches.length > 0
      ? 'warning'
      : undefined;

  const fieldId = `field-${findingPath}`;
  const elementCode = `${segment.id}${String(position).padStart(2, '0')}`;
  const codes = definition.codes ?? [];
  const codeListId = codes.length > 0 ? `${fieldId}-codes` : undefined;
  // Both derived from the same `value` (not the raw segment element) so a
  // composite's joined display string and an edited-but-not-yet-saved value
  // match and suggest consistently.
  const trimmedUpperValue = value.trim().toUpperCase();
  const matchedCode =
    trimmedUpperValue && !isComposite
      ? codes.find(c => c.code.toUpperCase() === trimmedUpperValue)
      : undefined;
  // Narrow the (potentially 500+ entry) code list down to ones relevant to
  // what's already typed before slicing to MAX_DATALIST_OPTIONS, so codes
  // later in list order (e.g. 'VN', 'ZZ') are still reachable as the user
  // types rather than only ever offering the first MAX_DATALIST_OPTIONS in
  // dictionary order.
  const suggestedCodes = trimmedUpperValue
    ? codes.filter(c => c.code.toUpperCase().includes(trimmedUpperValue))
    : codes;
  const codeMeaning = matchedCode?.label;
  const codeExplanation = matchedCode?.description ?? codeMeaning;
  const handleChange = (nextValue: string) =>
    onEdit(
      elementPathFor(
        groupIndex,
        transactionIndex,
        transaction,
        segment,
        position
      ),
      nextValue
    );

  const explanation = (
    <>
      <p className="font-medium">{definition.label}</p>
      <p className="font-mono text-[10px] text-muted-foreground">
        {[elementCode, definition.required && 'Required', definition.format]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p className="mt-1 text-muted-foreground">{definition.description}</p>
    </>
  );

  const inputClassName = cn(
    'text-sm',
    severity === 'error' && 'border-destructive/60',
    severity === 'warning' && 'border-warning/60'
  );

  return (
    <div className="grid grid-cols-[minmax(0,13rem)_minmax(0,1fr)] items-baseline gap-x-3 py-0.5">
      <Explain content={explanation}>
        <label
          htmlFor={editing ? fieldId : undefined}
          className="cursor-help truncate text-xs text-muted-foreground underline decoration-muted-foreground/30 decoration-dotted underline-offset-4"
        >
          {`${elementCode}/${definition.label}`}
        </label>
      </Explain>

      <div className="min-w-0 space-y-0.5">
        {editing ? (
          <div className="flex items-center gap-2">
            {isLongTextField(definition.label) ? (
              <Textarea
                id={fieldId}
                value={value}
                disabled={isComposite}
                autoFocus={autoFocus}
                onChange={e => handleChange(e.target.value)}
                className={cn('min-h-[56px] max-w-xl py-1', inputClassName)}
              />
            ) : (
              <Input
                id={fieldId}
                value={value}
                disabled={isComposite}
                autoFocus={autoFocus}
                list={codeListId}
                onChange={e => handleChange(e.target.value)}
                className={cn('h-8 min-w-[8rem] max-w-md px-2', inputClassName)}
              />
            )}
            {codeMeaning && (
              <Explain content={codeExplanation}>
                <span className="min-w-0 cursor-help truncate text-xs text-muted-foreground">
                  {codeMeaning}
                </span>
              </Explain>
            )}
          </div>
        ) : (
          <p className="break-words">
            <span
              id={fieldId}
              tabIndex={-1}
              className={cn(
                'rounded text-sm font-medium outline-none',
                severity && 'px-1',
                severity === 'error' && 'bg-destructive/15 text-destructive',
                severity === 'warning' && 'bg-warning/15 text-warning'
              )}
            >
              {value || '—'}
            </span>
            {codeMeaning && (
              <Explain content={codeExplanation}>
                <span className="ml-2 cursor-help text-xs text-muted-foreground">
                  {codeMeaning}
                </span>
              </Explain>
            )}
          </p>
        )}
        {codeListId && editing && (
          <datalist id={codeListId}>
            {suggestedCodes.slice(0, MAX_DATALIST_OPTIONS).map(c => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </datalist>
        )}
        {matches.map((finding, i) => {
          const Icon =
            finding.severity === 'error' ? OctagonAlert : AlertTriangle;
          return (
            <p
              key={i}
              className={cn(
                'flex items-start gap-1 text-xs',
                finding.severity === 'error'
                  ? 'text-destructive'
                  : 'text-warning'
              )}
            >
              <Icon className="mt-0.5 h-3 w-3 shrink-0" />
              {inlineMessage(finding.message)}
            </p>
          );
        })}
      </div>
    </div>
  );
}
